import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import {
  useNavigation,
  useRoute,
  useFocusEffect,
} from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RouteProp } from '@react-navigation/native';
import { MaterialCommunityIcons, MaterialIcons } from '@expo/vector-icons';

import type { RootStackParamList } from '../navigation/stackTypes';
import Header from '../components/Header';
import LoadingButton from '../components/LoadingButton';
import { isSuccessful } from '../services/api-requests';
import { showThemedAlert } from '../components/common/ThemedAlert';
import {
  disableRfidCard,
  enableRfidCard,
  getRfidCardTaps,
  listRfidCards,
  revokeRfidCard,
  updateRfidCard,
} from '../services/rfid-service';
import {
  ApiRfidCard,
  OUTCOME_COLORS,
  OUTCOME_LABELS,
  STATUS_COLORS,
  STATUS_LABELS,
  TapEvent,
  formatCardLabel,
  formatRelativeTime,
} from '../models/rfid-card-model';
import { notificationService } from '../services/notification-service';

type NavigationProp = NativeStackNavigationProp<
  RootStackParamList,
  'RfidCardSettings'
>;
type RouteProps = RouteProp<RootStackParamList, 'RfidCardSettings'>;

const RfidCardSettings: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const { cardUuid } = route.params;

  const [card, setCard] = useState<ApiRfidCard | null>(null);
  const [taps, setTaps] = useState<TapEvent[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);

  const [nicknameDraft, setNicknameDraft] = useState<string>('');
  const [savingNickname, setSavingNickname] = useState<boolean>(false);
  const [autoChargeBusy, setAutoChargeBusy] = useState<boolean>(false);
  const [statusBusy, setStatusBusy] = useState<boolean>(false);

  const fetchData = useCallback(async () => {
    try {
      const [cardsResp, tapsResp] = await Promise.all([
        listRfidCards(),
        getRfidCardTaps(cardUuid, 20),
      ]);
      if (isSuccessful(cardsResp.status) && cardsResp.payload) {
        const match = cardsResp.payload.cards.find((c) => c.uuid === cardUuid);
        if (match) {
          setCard(match);
          setNicknameDraft(match.nickname ?? '');
        } else {
          setCard(null);
        }
      }
      if (isSuccessful(tapsResp.status) && tapsResp.payload) {
        setTaps(tapsResp.payload.taps ?? []);
      }
    } catch (e) {
      console.error('[RfidCardSettings] fetch error:', e);
    }
  }, [cardUuid]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchData();
      setLoading(false);
    })();
  }, [fetchData]);

  // Refresh taps whenever the screen regains focus (e.g. after returning from a
  // notification deep-link).
  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [fetchData]),
  );

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  }, [fetchData]);

  const saveNickname = useCallback(async () => {
    if (!card) return;
    const next = nicknameDraft.trim();
    if (next === (card.nickname ?? '').trim()) return;
    setSavingNickname(true);
    try {
      const resp = await updateRfidCard(card.uuid, {
        nickname: next.length === 0 ? null : next,
      });
      if (isSuccessful(resp.status) && resp.payload?.card) {
        setCard(resp.payload.card);
        notificationService.sendNotification('success', 'Nickname updated.');
      } else {
        notificationService.sendNotification(
          'error',
          'Could not update nickname.',
        );
      }
    } finally {
      setSavingNickname(false);
    }
  }, [card, nicknameDraft]);

  const toggleAutoCharge = useCallback(
    async (next: boolean) => {
      if (!card) return;
      setAutoChargeBusy(true);
      try {
        const resp = await updateRfidCard(card.uuid, {
          auto_charge_enabled: next,
        });
        if (isSuccessful(resp.status) && resp.payload?.card) {
          setCard(resp.payload.card);
        } else {
          notificationService.sendNotification(
            'error',
            'Could not update auto-charge setting.',
          );
        }
      } finally {
        setAutoChargeBusy(false);
      }
    },
    [card],
  );

  const onDisable = useCallback(() => {
    if (!card) return;
    showThemedAlert(
      'Disable this tag?',
      'Disabled tags can be re-enabled at any time. Taps will be declined until then.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Disable',
          style: 'destructive',
          onPress: async () => {
            setStatusBusy(true);
            try {
              const resp = await disableRfidCard(card.uuid);
              if (isSuccessful(resp.status) && resp.payload?.card) {
                setCard(resp.payload.card);
                notificationService.sendNotification('success', 'Tag disabled.');
              } else {
                notificationService.sendNotification(
                  'error',
                  'Could not disable tag.',
                );
              }
            } finally {
              setStatusBusy(false);
            }
          },
        },
      ],
      'warning',
    );
  }, [card]);

  const onEnable = useCallback(async () => {
    if (!card) return;
    setStatusBusy(true);
    try {
      const resp = await enableRfidCard(card.uuid);
      if (isSuccessful(resp.status) && resp.payload?.card) {
        setCard(resp.payload.card);
        notificationService.sendNotification('success', 'Tag re-enabled.');
      } else if (resp.status === 409) {
        notificationService.sendNotification(
          'error',
          'Only disabled tags can be re-enabled.',
        );
      } else {
        notificationService.sendNotification('error', 'Could not enable tag.');
      }
    } finally {
      setStatusBusy(false);
    }
  }, [card]);

  const onRevoke = useCallback(() => {
    if (!card) return;
    showThemedAlert(
      'Permanently revoke this tag?',
      'Revoked tags cannot be re-paired and will disappear from your list. This cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Revoke',
          style: 'destructive',
          onPress: async () => {
            setStatusBusy(true);
            try {
              const resp = await revokeRfidCard(card.uuid);
              if (isSuccessful(resp.status)) {
                notificationService.sendNotification('success', 'Tag revoked.');
                navigation.goBack();
              } else {
                notificationService.sendNotification(
                  'error',
                  'Could not revoke tag.',
                );
              }
            } finally {
              setStatusBusy(false);
            }
          },
        },
      ],
      'error',
    );
  }, [card, navigation]);

  const headerLabel = useMemo(
    () => (card ? formatCardLabel(card) : 'Tag Details'),
    [card],
  );

  if (loading) {
    return (
      <SafeAreaView style={styles.container} edges={['top']}>
        {Platform.OS !== 'web' && <Header headerText="Tag Details" />}
        <View style={styles.centered}>
          <ActivityIndicator size="large" color="#40BFB4" />
        </View>
      </SafeAreaView>
    );
  }

  if (!card) {
    return (
      <SafeAreaView style={styles.container} edges={['top']}>
        {Platform.OS !== 'web' && <Header headerText="Tag Details" />}
        <View style={styles.centered}>
          <MaterialIcons name="error-outline" size={48} color="#8D8E90" />
          <Text style={styles.notFoundText}>Tag not found.</Text>
        </View>
      </SafeAreaView>
    );
  }

  const statusColor = STATUS_COLORS[card.status];
  const statusLabel = STATUS_LABELS[card.status];
  const isDisabled = card.status === 'DISABLED';
  const isPending = card.status === 'PENDING_CLAIM';
  const isActive = card.status === 'ACTIVE';

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      {Platform.OS !== 'web' && <Header headerText={headerLabel} />}

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor="#40BFB4"
          />
        }
      >
        <View style={styles.heroCard}>
          <View style={styles.heroIcon}>
            <MaterialCommunityIcons
              name="contactless-payment"
              size={36}
              color="#40BFB4"
            />
          </View>
          <View style={styles.heroBody}>
            <Text style={styles.heroLabel} numberOfLines={1}>
              {formatCardLabel(card)}
            </Text>
            <View style={styles.heroMeta}>
              <View
                style={[
                  styles.statusPill,
                  { backgroundColor: statusColor + '22' },
                ]}
              >
                <View
                  style={[styles.statusDot, { backgroundColor: statusColor }]}
                />
                <Text style={[styles.statusText, { color: statusColor }]}>
                  {statusLabel}
                </Text>
              </View>
              {card.last4_uid && (
                <Text style={styles.uidText}>UID ••{card.last4_uid}</Text>
              )}
            </View>
            <Text style={styles.heroSub}>
              Last used {formatRelativeTime(card.last_used_at)}
            </Text>
          </View>
        </View>

        {isPending && (
          <View style={styles.infoBanner}>
            <MaterialIcons name="info" size={18} color="#40BFB4" />
            <Text style={styles.infoBannerText}>
              This tag hasn&apos;t been tapped yet. Tap it on any Meander
              charger within 60 seconds of starting pairing to finish setup.
            </Text>
          </View>
        )}

        <Text style={styles.sectionTitle}>Nickname</Text>
        <View style={styles.row}>
          <TextInput
            style={styles.input}
            value={nicknameDraft}
            onChangeText={setNicknameDraft}
            placeholder="Add a nickname"
            placeholderTextColor="#6C6F75"
            maxLength={40}
            editable={!savingNickname}
          />
          <LoadingButton
            onPress={saveNickname}
            isLoading={savingNickname}
            disabled={(card.nickname ?? '').trim() === nicknameDraft.trim()}
            backgroundColor="#40BFB4"
            style={styles.saveButton}
          >
            Save
          </LoadingButton>
        </View>

        <Text style={styles.sectionTitle}>Auto-charge</Text>
        <View style={styles.settingRow}>
          <View style={styles.settingCopy}>
            <Text style={styles.settingTitle}>Tap to charge automatically</Text>
            <Text style={styles.settingSubtitle}>
              When on, taps authorize your default payment method instantly.
              When off, taps are logged but you must confirm in the app to
              start a session.
            </Text>
          </View>
          <Switch
            value={card.auto_charge_enabled}
            onValueChange={toggleAutoCharge}
            disabled={autoChargeBusy || !isActive}
            trackColor={{ false: '#3A3D42', true: '#40BFB4' }}
            thumbColor={Platform.OS === 'android' ? '#FFFFFF' : undefined}
          />
        </View>

        <Text style={styles.sectionTitle}>Status</Text>
        <View style={styles.actionGroup}>
          {isDisabled ? (
            <LoadingButton
              onPress={onEnable}
              isLoading={statusBusy}
              backgroundColor="#10B981"
              style={styles.actionButton}
            >
              Enable tag
            </LoadingButton>
          ) : isActive ? (
            <LoadingButton
              onPress={onDisable}
              isLoading={statusBusy}
              backgroundColor="#3A3D42"
              style={styles.actionButton}
            >
              Disable tag
            </LoadingButton>
          ) : null}
          {!isPending && (
            <LoadingButton
              onPress={onRevoke}
              isLoading={statusBusy}
              backgroundColor="#7F1D1D"
              style={styles.actionButton}
            >
              Revoke tag
            </LoadingButton>
          )}
        </View>

        <Text style={styles.sectionTitle}>Recent taps</Text>
        {taps.length === 0 ? (
          <View style={styles.emptyState}>
            <MaterialCommunityIcons
              name="history"
              size={28}
              color="#8D8E90"
            />
            <Text style={styles.emptyText}>No taps yet.</Text>
          </View>
        ) : (
          <View style={styles.tapsList}>
            {taps.map((tap, idx) => {
              const color = OUTCOME_COLORS[tap.outcome];
              const label = OUTCOME_LABELS[tap.outcome];
              return (
                <Pressable
                  key={`${tap.created_at}-${idx}`}
                  disabled={!tap.order_uuid}
                  onPress={() => {
                    // No direct order detail screen, but we can route to the
                    // charger so users get context. The order_uuid is exposed
                    // for analytics / support reference.
                  }}
                  style={({ pressed }) => [
                    styles.tapRow,
                    { opacity: pressed && tap.order_uuid ? 0.85 : 1 },
                  ]}
                >
                  <View style={[styles.tapDot, { backgroundColor: color }]} />
                  <View style={styles.tapBody}>
                    <Text style={styles.tapTitle}>{label}</Text>
                    {tap.reason ? (
                      <Text style={styles.tapReason} numberOfLines={2}>
                        {tap.reason}
                      </Text>
                    ) : null}
                    <Text style={styles.tapTime}>
                      {formatRelativeTime(tap.created_at)} ·{' '}
                      {tap.charger_uuid.slice(0, 8)}…
                    </Text>
                  </View>
                </Pressable>
              );
            })}
          </View>
        )}

        <View style={{ height: 32 }} />
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#1A1D21' },
  scroll: { flex: 1 },
  scrollContent: { paddingHorizontal: 16, paddingBottom: 40 },
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center', gap: 10 },
  notFoundText: {
    color: '#8D8E90',
    fontSize: 15,
    fontFamily: 'PlusJakartaSans-Regular',
  },
  heroCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: '#292D33',
    borderRadius: 14,
    padding: 16,
    marginTop: 12,
    marginBottom: 16,
  },
  heroIcon: {
    width: 56,
    height: 56,
    borderRadius: 12,
    backgroundColor: '#40BFB420',
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroBody: { flex: 1, gap: 6 },
  heroLabel: {
    color: '#FFFFFF',
    fontSize: 18,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '700',
  },
  heroMeta: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  heroSub: {
    color: '#8D8E90',
    fontSize: 12,
    fontFamily: 'PlusJakartaSans-Regular',
  },
  uidText: {
    color: '#8D8E90',
    fontSize: 11,
    fontFamily: 'PlusJakartaSans-Regular',
    letterSpacing: 0.5,
  },
  statusPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
  },
  statusDot: { width: 6, height: 6, borderRadius: 3 },
  statusText: {
    fontSize: 11,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '700',
  },
  infoBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    backgroundColor: '#0F3635',
    borderRadius: 12,
    padding: 12,
    borderLeftWidth: 3,
    borderLeftColor: '#40BFB4',
    marginBottom: 16,
  },
  infoBannerText: {
    flex: 1,
    color: '#B0E5DF',
    fontSize: 12,
    fontFamily: 'PlusJakartaSans-Regular',
    lineHeight: 17,
  },
  sectionTitle: {
    color: '#8D8E90',
    fontSize: 12,
    fontFamily: 'PlusJakartaSans-Regular',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginTop: 16,
    marginBottom: 8,
  },
  row: { flexDirection: 'row', alignItems: 'stretch', gap: 8 },
  input: {
    flex: 1,
    backgroundColor: '#292D33',
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: Platform.OS === 'ios' ? 12 : 10,
    color: '#FFFFFF',
    fontSize: 15,
    fontFamily: 'PlusJakartaSans-Regular',
    borderWidth: 1,
    borderColor: '#3A3D42',
  },
  saveButton: { borderRadius: 10, paddingHorizontal: 16, paddingVertical: 0 },
  settingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: '#292D33',
    borderRadius: 12,
    padding: 14,
  },
  settingCopy: { flex: 1 },
  settingTitle: {
    color: '#FFFFFF',
    fontSize: 15,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '600',
    marginBottom: 2,
  },
  settingSubtitle: {
    color: '#8D8E90',
    fontSize: 12,
    fontFamily: 'PlusJakartaSans-Regular',
    lineHeight: 16,
  },
  actionGroup: { gap: 10 },
  actionButton: { borderRadius: 12, paddingVertical: 4 },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 20,
    gap: 6,
    backgroundColor: '#292D33',
    borderRadius: 12,
  },
  emptyText: {
    color: '#8D8E90',
    fontSize: 13,
    fontFamily: 'PlusJakartaSans-Regular',
  },
  tapsList: { gap: 8 },
  tapRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    backgroundColor: '#292D33',
    borderRadius: 12,
    padding: 12,
  },
  tapDot: { width: 10, height: 10, borderRadius: 5, marginTop: 4 },
  tapBody: { flex: 1 },
  tapTitle: {
    color: '#FFFFFF',
    fontSize: 14,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '600',
    marginBottom: 2,
  },
  tapReason: {
    color: '#B0B3B8',
    fontSize: 12,
    fontFamily: 'PlusJakartaSans-Regular',
    lineHeight: 16,
    marginBottom: 2,
  },
  tapTime: {
    color: '#6C6F75',
    fontSize: 11,
    fontFamily: 'PlusJakartaSans-Regular',
  },
});

export default RfidCardSettings;
